import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ImageModule } from 'primeng/image';
import { SelectModule } from 'primeng/select';

import { AppMenuitem } from './app.menuitem';
import { BaseComponent, BASE_IMPORTS } from '../../shared/components/base-component/base-component';
import { LanguageService } from '../../shared/services/language.service';
import { SidebarService } from '../../shared/services/sidebar.service';
import { SafeAny } from '../../shared/types/safe-any';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [
    ...BASE_IMPORTS,
    AppMenuitem,
    SelectModule,
    ButtonModule,
    ImageModule,
    DialogModule,
    IftaLabelModule,
    FormsModule,
    DividerModule,
  ],
  template: `<ul class="layout-menu">
    <div
      class="pt-4 px-[10px] flex align-items-start w-full cursor-pointer"
      routerLink="/profilo"
      [class.active-route]="
        router.isActive('/profilo', {
          paths: 'subset',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        })
      "
    >
      <i class="pi pi-user my-large-icon text-white me-3"></i>
      <a class="text-white font-bold ">ADMIN</a>
    </div>
    <p-divider />
    <div class="h-full w-full flex flex-col justify-between">
      @for (item of topMenu; track $index; let i = $index) {
        @if (!item.separator) {
          <app-menuitem [item]="item" [index]="i" [root]="true"></app-menuitem>
        }
      }
      @for (item of bottomMenu; track $index; let i = $index) {
        @if (!item.separator) {
          <app-menuitem [item]="item" [index]="i" [root]="true"></app-menuitem>
        }
      }
    </div>

    <div class="sidebar-footer w-full">
      <div class="sidebar-logout w-full">
        <p-button
          [label]="'common.logout' | translate"
          icon="pi pi-sign-out"
          iconPos="right"
          severity="secondary"
          styleClass="btn-menu-exit"
          [style]="{ width: '100%' }"
          outlined
          (click)="onLogout()"
        />
      </div>
      <img class="mx-auto" src="assets/img/rl-logo.svg" alt="logo" width="200px" />
    </div>
  </ul>`,
  styles: [
    `
      .active-route a,
      .active-route i {
        color: #a0cbb1;
      }

      .sidebar-footer {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
        margin-top: auto;
      }
    `,
  ],
})
export class AppMenu extends BaseComponent {
  private readonly languageService = inject(LanguageService);
  private readonly http = inject(HttpClient);
  topMenu: MenuItem[] = [];
  bottomMenu: MenuItem[] = [];
  languages: { label: string; value: string }[] = [];
  selectedLanguage: string = 'it';

  translationEntity: SafeAny = {
    dashboard: {
      title: 'dashboard.title',
    },
    customers: {
      title: 'customers.title',
    },
    operators: {
      title: 'operators.title',
    },
    tickets: {
      title: 'tickets.title',
    },
    language: {
      title: 'language.title',
      italian: 'language.italian',
      english: 'language.english',
    },
  };

  constructor(private readonly sidebarService: SidebarService) {
    super();
    const translationKeys: string[] = [];
    const collectKeys = (obj: SafeAny, prefix = '') => {
      for (const key in obj) {
        if (typeof obj[key] === 'string') {
          translationKeys.push(obj[key]);
        } else if (typeof obj[key] === 'object') {
          collectKeys(obj[key], prefix);
        }
      }
    };
    collectKeys(this.translationEntity);

    this.translateService.stream(translationKeys).subscribe((res) => {
      const updateTranslations = (obj: SafeAny): SafeAny => {
        const result: SafeAny = {};
        for (const key in obj) {
          if (typeof obj[key] === 'string') {
            result[key] = res[obj[key]] ?? obj[key];
          } else if (typeof obj[key] === 'object') {
            result[key] = updateTranslations(obj[key]);
          }
        }
        return result;
      };
      this.translationEntity = updateTranslations(this.translationEntity);
      this.updateLanguageField();
    });
  }

  override ngOnInit(): void {
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    this.updateLanguageField();
  }

  private updateLanguageField(): void {
    this.topMenu = [
      {
        items: [
          {
            label: this.translationEntity.dashboard.title,
            icon: 'pi pi-fw pi-objects-column',
            routerLink: ['/dashboard'],
          },
          {
            label: this.translationEntity.customers.title,
            icon: 'pi pi-fw pi-users',
            routerLink: ['/clienti'],
          },
          {
            label: this.translationEntity.operators.title,
            icon: 'pi pi-list',
            routerLink: ['/operatori'],
          },
          {
            label: this.translationEntity.tickets.title,
            icon: 'pi pi-list',
            routerLink: ['/ticket'],
          },
        ],
      },
    ];

    this.languages = [
      { label: this.translationEntity.language.italian, value: 'it' },
      { label: this.translationEntity.language.english, value: 'en' },
    ];
  }

  onLanguageChange(event: SafeAny) {
    this.languageService.setLanguage(event);
  }

  onLogout(): void {
    this.AuthService.logOut().subscribe({
      next: (response) => {
        if (response.success) {
          this.MessageService.add({
            detail: response.message,
          });
        }
      },
      error: (error) => {
        if (error.error?.message) {
          this.MessageService.add({
            detail: error.error.message,
          });
        }
      },
    });
  }
}
